import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '@/lib/supabase'
import { getAppOrigin } from '@/lib/app-url'

export interface User {
  id: string
  email: string
  name: string
  display_name?: string
  avatar_url?: string
  profile_image_url?: string
  createdAt: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  pendingConfirmationEmail: string | null
  loading: boolean
  error: string | null
  initialized: boolean
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>
  register: (email: string, password: string, name?: string) => Promise<boolean>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  initialize: () => Promise<void>
  updateProfile: (profileData: Partial<User>) => Promise<boolean>
  clearError: () => void
  clearPendingConfirmation: () => void
  setLoading: (loading: boolean) => void
}

type AuthStore = AuthState & AuthActions

let authSubscriptionInitialized = false

const isEmailConfirmed = (authUser: any) => Boolean(authUser?.email_confirmed_at)

const toStoreUser = (authUser: any): User => {
  const metadata = authUser.user_metadata ?? {}
  const email = authUser.email ?? ''
  const fallbackName = email.split('@')[0] || 'Gebruiker'

  return {
    id: authUser.id,
    email,
    name: metadata.name || metadata.display_name || fallbackName,
    display_name: metadata.display_name || metadata.name || fallbackName,
    avatar_url: metadata.avatar_url,
    profile_image_url: metadata.profile_image_url,
    createdAt: authUser.created_at ?? new Date().toISOString(),
  }
}

const applyAuthUser = (set: (partial: Partial<AuthState>) => void, authUser: any | null) => {
  if (!authUser) {
    set({
      user: null,
      isAuthenticated: false,
      pendingConfirmationEmail: null,
      loading: false,
      error: null,
      initialized: true,
    })
    return
  }

  if (!isEmailConfirmed(authUser)) {
    set({
      user: null,
      isAuthenticated: false,
      pendingConfirmationEmail: authUser.email ?? null,
      loading: false,
      error: 'Bevestig eerst je e-mailadres voordat je de app gebruikt.',
      initialized: true,
    })
    return
  }

  set({
    user: toStoreUser(authUser),
    isAuthenticated: true,
    pendingConfirmationEmail: null,
    loading: false,
    error: null,
    initialized: true,
  })
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      pendingConfirmationEmail: null,
      loading: true,
      error: null,
      initialized: false,

      login: async (email: string, password: string) => {
        set({ loading: true, error: null })
        const { data, error } = await supabase.auth.signInWithPassword({ email, password })

        if (error || !data.user) {
          set({
            loading: false,
            error: error?.message || 'Login failed',
          })
          return false
        }

        if (!isEmailConfirmed(data.user)) {
          set({
            user: null,
            isAuthenticated: false,
            pendingConfirmationEmail: data.user.email ?? email,
            loading: false,
            error: 'Bevestig eerst je e-mailadres via de link in de e-mail voordat je inlogt.',
            initialized: true,
          })
          return false
        }

        applyAuthUser(set, data.user)
        return true
      },

      register: async (email: string, password: string, name?: string) => {
        set({ loading: true, error: null })
        const appOrigin = getAppOrigin()
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: appOrigin ? `${appOrigin}/login?confirmed=1` : undefined,
            data: {
              name: name || email.split('@')[0],
              display_name: name || email.split('@')[0],
            },
          },
        })

        if (error || !data.user) {
          set({
            loading: false,
            error: error?.message || 'Registration failed',
          })
          return false
        }

        if (!data.session || !isEmailConfirmed(data.user)) {
          set({
            user: null,
            isAuthenticated: false,
            pendingConfirmationEmail: data.user.email ?? email,
            loading: false,
            error: null,
            initialized: true,
          })
          return true
        }

        applyAuthUser(set, data.user)
        return true
      },

      logout: async () => {
        await supabase.auth.signOut()
        applyAuthUser(set, null)
      },

      checkAuth: async () => {
        const { data, error } = await supabase.auth.getSession()
        if (error) {
          set({
            loading: false,
            error: error.message,
            isAuthenticated: false,
            user: null,
            initialized: true,
          })
          return
        }

        applyAuthUser(set, data.session?.user ?? null)
      },

      initialize: async () => {
        if (!authSubscriptionInitialized) {
          authSubscriptionInitialized = true
          supabase.auth.onAuthStateChange((_event, session) => {
            applyAuthUser(set, session?.user ?? null)
          })
        }

        if (!get().initialized) {
          await get().checkAuth()
        }
      },

      updateProfile: async (profileData: Partial<User>) => {
        const { user } = get()
        if (!user) {
          set({ error: 'Not authenticated' })
          return false
        }

        set({ loading: true, error: null })
        const currentUser = await supabase.auth.getUser()
        const currentMetadata = currentUser.data.user?.user_metadata ?? {}

        const { data, error } = await supabase.auth.updateUser({
          email: profileData.email && profileData.email !== user.email ? profileData.email : undefined,
          data: {
            ...currentMetadata,
            ...(profileData.name !== undefined ? { name: profileData.name } : {}),
            ...(profileData.display_name !== undefined ? { display_name: profileData.display_name } : {}),
            ...(profileData.avatar_url !== undefined ? { avatar_url: profileData.avatar_url } : {}),
            ...(profileData.profile_image_url !== undefined ? { profile_image_url: profileData.profile_image_url } : {}),
          },
        })

        if (error || !data.user) {
          set({
            loading: false,
            error: error?.message || 'Failed to update profile',
          })
          return false
        }

        applyAuthUser(set, data.user)
        return true
      },

      clearError: () => set({ error: null }),
      clearPendingConfirmation: () => set({ pendingConfirmationEmail: null }),
      setLoading: (loading: boolean) => set({ loading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        pendingConfirmationEmail: state.pendingConfirmationEmail,
        initialized: state.initialized,
      }),
    }
  )
)
